import { tCustomer } from "../data/Customers";
import { tTeamMember } from "../data/Team";
import { tIssue } from "../data/Issues";
import { GameConfig } from "../App";

export type UnlockedFeatures = {
    customerFeedback: boolean;
    finance: boolean;
    epics: boolean;
    velocity: boolean;
    teamMembers: boolean;
    release: boolean;
    backlogFilter: boolean;
};

interface HeaderProps {
    bank: number;
    openFinanceReportCallback: () => void;
    openCustomerReportCallback: () => void;
    openVelocityReportCallback: () => void;
    openTeamMemberReportCallback: () => void;
    openReleaseReportCallback: () => void;
    openEpicReportCallback: () => void;
    openFeedbackCallback: () => void;
    customers: tCustomer[];
    team: tTeamMember[];
    sprint: number;
    sprintVelocity: number[];
    releaseVersion: number;
    issues: tIssue[];
    customerFeedback: string[];
    unlockedFeatures: UnlockedFeatures;
    gameConfig?: GameConfig | null;
}

export default function Header(props: HeaderProps) {
    const bankColor = props.bank >= 0 ? 'black' : 'red';
    const version = require('../../package.json').version;
    
    // Calculate prospects and customers
    const prospects = props.customers.filter(c => !c.isCustomer && c.available <= props.sprint).length;
    const customers = props.customers.filter(c => c.isCustomer).length;
    
    // Calculate average velocity (last 3 sprints or fewer if less than 3)
    const calculateAverageVelocity = () => {
        if (props.sprintVelocity.length === 0) {
            return 15; // Default if no sprints yet
        } else {
            const lastThreeSprints = props.sprintVelocity.slice(-3);
            const sum = lastThreeSprints.reduce((acc, velocity) => acc + velocity, 0);
            return Math.round(sum / lastThreeSprints.length);
        }
    };
    
    const averageVelocity = calculateAverageVelocity();
    
    // Calculate total skill level of developers (sum of all team member levels)
    const totalSkillLevel = props.team.reduce((sum, member) => sum + member.level, 0).toFixed(2);
    
    // Calculate epics completion (excluding "Misc")
    const uniqueEpics = Array.from(new Set(props.issues.filter(issue => issue.epic !== 'Misc').map((issue) => issue.epic)));
    const completedEpics = uniqueEpics.filter(epic => {
        const epicIssues = props.issues.filter(issue => issue.epic === epic && issue.available <= props.sprint);
        const releasedIssues = epicIssues.filter(issue => issue.released === true);
        return epicIssues.length > 0 && epicIssues.length === releasedIssues.length;
    }).length;
    const totalEpics = uniqueEpics.length;
    
    return (
        <header className="header">
            <div className="header-title-section">
                <div className="header-icon">📊</div>
                <div className="header-text">
                    {props.gameConfig ? (
                        <>
                            <h1 style={{ color: props.gameConfig.teamColor }}>{props.gameConfig.teamName}</h1>
                            <div className="header-subtitle">{props.gameConfig.gameName}</div>
                        </>
                    ) : (
                        <>
                            <h1>Idle Sprint Planning Simulator</h1>
                            <div className="header-version">v{version}</div>
                        </>
                    )}
                </div>
            </div>
            <div className="header-reports-section">
                {props.unlockedFeatures.release && (
                    <div className="report-icon" onClick={props.openReleaseReportCallback} data-testid="release-report-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer' }}>🚀</span>
                        <p style={{ margin: '5px 0 0 0' }}>
                            {props.releaseVersion.toFixed(1)}
                        </p>
                    </div>
                )}
                {props.unlockedFeatures.teamMembers && (
                    <div className="report-icon" onClick={props.openTeamMemberReportCallback} data-testid="team-member-report-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer' }}>⚒️</span>
                        <p style={{ margin: '5px 0 0 0' }}>
                            {totalSkillLevel}
                        </p>
                    </div>
                )}
                {props.unlockedFeatures.velocity && (
                    <div className="report-icon" onClick={props.openVelocityReportCallback} data-testid="velocity-report-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer' }}>📈</span>
                        <p style={{ margin: '5px 0 0 0' }}>
                            {averageVelocity}
                        </p>
                    </div>
                )}
                {props.unlockedFeatures.customerFeedback && (
                    <div className="report-icon" onClick={props.openCustomerReportCallback} data-testid="customer-report-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer' }}>📋</span>
                        <p style={{ margin: '5px 0 0 0' }}>
                            <span style={{ color: '#ff9800' }}>{prospects}</span>
                            <span>/</span>
                            <span style={{ color: '#4caf50' }}>{customers}</span>
                        </p>
                    </div>
                )}
                {props.unlockedFeatures.customerFeedback && (
                    <div className="report-icon" onClick={props.openFeedbackCallback} data-testid="feedback-report-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer', position: 'relative' }}>
                            🗨️
                            {props.customerFeedback.length > 0 && (
                                <span className="feedback-badge">{props.customerFeedback.length}</span>
                            )}
                        </span>
                        <p style={{ margin: '5px 0 0 0' }}>
                            {props.customerFeedback.length}
                        </p>
                    </div>
                )}
                {props.unlockedFeatures.epics && (
                    <div className="report-icon" onClick={props.openEpicReportCallback} data-testid="epic-report-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer' }}>🏆</span>
                        <p style={{ margin: '5px 0 0 0' }}>
                            {completedEpics}/{totalEpics}
                        </p>
                    </div>
                )}
                {props.unlockedFeatures.finance && (
                    <div className="report-icon" onClick={props.openFinanceReportCallback} data-testid="bank-icon">
                        <span style={{ fontSize: '24px', cursor: 'pointer' }}>🏦</span>
                        <p style={{ margin: '5px 0 0 0', color: bankColor }}>£{props.bank}</p>
                    </div>
                )}
            </div>
        </header>
    )
}